import React, { useState, useEffect, useCallback, useRef } from "react";
import { addResult, deleteResult } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function AdministrativeResultAdminPanel({ darkMode }) {
  const { threeTable, fetchThreeTable, fetchThreeTableSection, threeTableHasMore } = useGlobalData();
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({ jobSlug: "" });
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (!threeTable?.jobs?.length) {
      fetchThreeTable();
    }
  }, [threeTable?.jobs?.length, fetchThreeTable]);

  useEffect(() => {
    setJobs(threeTable.jobs || []);
  }, [threeTable.jobs]);

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      setIsLoading(true);
      try {
        await fetchThreeTableSection("results", false, { limit: PAGE_LIMIT, offset: 0 });
      } finally {
        if (!ignore) setIsLoading(false);
      }
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll fetch
  const fetchMoreResults = useCallback(async () => {
    setIsFetchingMore(true);
    try {
      const offset = Array.isArray(threeTable.results) ? threeTable.results.length : 0;
      await fetchThreeTableSection("results", false, { limit: PAGE_LIMIT, offset });
    } finally {
      setIsFetchingMore(false);
    }
  }, [fetchThreeTableSection, threeTable.results]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!threeTableHasMore.results || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreResults();
    }
  }, [threeTableHasMore.results, isFetchingMore, fetchMoreResults]);

  // Attach scroll event
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addResult(formData);
    alert("Result added!");
    setFormData({ jobSlug: "" });
    // Refresh global state (replace with first page)
    await fetchThreeTableSection("results", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this result?")) {
      await deleteResult(id);
      alert("Result deleted!");
      // Refresh global state (replace with first page)
      await fetchThreeTableSection("results", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const getJobTitle = (jobSlug) => jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;
  const filteredResults = Array.isArray(threeTable.results)
    ? threeTable.results.filter((result) => !formData.jobSlug || result.jobSlug === formData.jobSlug)
    : [];

  if (role !== "administrative") {
    return <div className="admin-panel-error">❌ Access denied! Only Administrative users can access this panel.</div>;
  }

  return (
    <div className="resource-admin-panel resource-admin-panel--red">
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">Administrative Result Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Yahan se result entries ko manage karo. Job choose karke result add karo, filtered list me dekho aur live page ko open ya delete karo.</p>
        </div>
        <div className="resource-admin-panel__pill">Results</div>
      </div>

      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Job:</label>
          <select
            value={formData.jobSlug}
            onChange={(e) => setFormData({ ...formData, jobSlug: e.target.value })}
            required
          >
            <option value="">Select Job</option>
            {jobs.map((job) => (
              <option key={job.slug} value={job.slug}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
        <button type="submit">Add Result</button>
      </form>

      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Result Entries</h3>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ul>
            {filteredResults.map((result) => {
              const isPending = result.status === "pending";
              const statusColor = isPending ? "#f7b500" : "#2ecc40";
              return (
                <li key={result._id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span>{getJobTitle(result.jobSlug)}</span>
                  <span style={{
                    background: statusColor,
                    color: "#fff",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontWeight: 600,
                    fontSize: 13
                  }}>
                    {result.status === "pending" ? "Pending" : "Approved"}
                  </span>
                  {isPending && (
                    <button onClick={() => handleDelete(result._id)} style={{ marginLeft: 8 }}>Delete</button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!threeTableHasMore.results && !isLoading && filteredResults.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more results.</div>}
      </div>
    </div>
  );
}

export default AdministrativeResultAdminPanel;
